import React, { useState, useRef, useEffect, useCallback } from 'react'
import { streamChatMessage, getDraft, getSessionMessages, updateSessionModel, ChatContext, Draft } from '../lib/api'
import DraftEditor from './DraftEditor'

interface SelectedItem {
  type: string        // "feed_item" | "action_item" | "contact" | "document" | "onedrive_file"
  id?: number
  onedrivePath?: string
  label?: string
}

interface Message {
  role: 'user' | 'assistant'
  content: string
  images?: string[]  // Base64 image data URIs
}

interface BulkItem {
  type: string
  id: number
  label?: string
}

interface Props {
  view: string
  selectedItem?: SelectedItem | null
  selectedItems?: BulkItem[]
}

const PANEL_WIDTH = 380
const MIN_READ_PX = 80
const MIN_WRITE_PX = 80

export default function ContextChat({ view, selectedItem, selectedItems }: Props) {
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [streamingText, setStreamingText] = useState('')
  const [statusText, setStatusText] = useState('')
  const [sessionId, setSessionId] = useState<number | undefined>(undefined)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [pastedImages, setPastedImages] = useState<string[]>([])  // Base64 data URIs of pasted images
  const [selectedModel, setSelectedModel] = useState<string>(() =>
    localStorage.getItem('hq-model') || 'claude-sonnet-4-6'
  )

  // Divider: readFlex controls the flex ratio of read vs write area
  // readFlex=3, writeFlex=1 → 75% read / 25% write by default
  const [readFlex, setReadFlex] = useState(3)
  const [writeFlex, setWriteFlex] = useState(1)
  const panelRef = useRef<HTMLDivElement>(null)
  const dividerRef = useRef<HTMLDivElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const messageQueue = useRef<Array<{ text: string; images?: string[] }>>([])

  // Toggle body class so page content can shrink when panel is open
  useEffect(() => {
    document.body.classList.toggle('inspector-open', open)
    return () => { document.body.classList.remove('inspector-open') }
  }, [open])

  // Build a localStorage key scoped to the current context
  function sessionStorageKey(): string | null {
    if (selectedItem?.id) return `hq-chat-${view}-${selectedItem.type}-${selectedItem.id}`
    if (selectedItem?.onedrivePath) return `hq-chat-${view}-onedrive-${selectedItem.onedrivePath}`
    if (!selectedItem) return `hq-chat-${view}-general`
    return null
  }

  // Auto-open and restore history when item selected
  useEffect(() => {
    setMessages([])
    setStreamingText('')
    setDraft(null)
    setSessionId(undefined)

    const key = selectedItem?.id
      ? `hq-chat-${view}-${selectedItem.type}-${selectedItem.id}`
      : selectedItem?.onedrivePath
        ? `hq-chat-${view}-onedrive-${selectedItem.onedrivePath}`
        : null

    if (selectedItem) {
      setOpen(true)
      setTimeout(() => textareaRef.current?.focus(), 50)
    }

    // Restore prior session from localStorage
    if (key) {
      const stored = localStorage.getItem(key)
      if (stored) {
        const sid = parseInt(stored, 10)
        if (!isNaN(sid)) {
          setSessionId(sid)
          getSessionMessages(sid)
            .then(data => {
              if (data.messages?.length) {
                setMessages(data.messages.map((m: { role: 'user' | 'assistant'; content: string; images?: string[] }) => ({
                  role: m.role,
                  content: m.content,
                  images: m.images,
                })))
              }
            })
            .catch(() => {
              // Session gone — clear stale key
              localStorage.removeItem(key)
              setSessionId(undefined)
            })
        }
      }
    }
  }, [selectedItem?.id, selectedItem?.onedrivePath])

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streamingText])

  const placeholder = selectedItems?.length
    ? `Ask HQ about these ${selectedItems.length} item${selectedItems.length === 1 ? '' : 's'}...`
    : selectedItem?.label
      ? `Ask about ${selectedItem.label}...`
      : `Ask HQ about this ${view}...`

  // Build context — bulk selection takes precedence over single-item click
  const context: ChatContext | null = selectedItems?.length
    ? { view, selected_items: selectedItems }
    : selectedItem
      ? { view, item_type: selectedItem.type, item_id: selectedItem.id, onedrive_path: selectedItem.onedrivePath }
      : { view }

  async function sendMessage(text: string, images?: string[]) {
    setStreaming(true)
    setStreamingText('')
    setDraft(null)
    setMessages(prev => [...prev, { role: 'user', content: text, images }])

    let full = ''
    try {
      for await (const chunk of streamChatMessage(text, sessionId, context, images, sessionId ? null : selectedModel)) {
        if (chunk.type === 'status') {
          setStatusText(chunk.content ?? '')
        } else if (chunk.type === 'text') {
          setStatusText('')
          full += chunk.content ?? ''
          setStreamingText(full)
        } else if (chunk.type === 'done') {
          setSessionId(chunk.session_id)
          const key = sessionStorageKey()
          if (key && chunk.session_id) {
            localStorage.setItem(key, String(chunk.session_id))
          }
          setStreamingText('')
          setMessages(prev => [...prev, { role: 'assistant', content: full }])
          if (chunk.draft_id) {
            try {
              const d = await getDraft(chunk.draft_id)
              setDraft(d)
            } catch {}
          }
        }
      }
    } catch {
      setStreamingText('')
      setMessages(prev => [...prev, { role: 'assistant', content: 'Error — try again.' }])
    } finally {
      // Drain queue — send next queued message if any
      const next = messageQueue.current.shift()
      if (next) {
        await sendMessage(next.text, next.images)
      } else {
        setStreaming(false)
      }
    }
  }

  function send() {
    const text = input.trim()
    if (!text && pastedImages.length === 0) return
    const images = pastedImages.length > 0 ? [...pastedImages] : undefined
    setInput('')
    setPastedImages([])

    if (streaming) {
      // Queue the message — it will be sent when current response finishes
      messageQueue.current.push({ text, images })
      setMessages(prev => [...prev, { role: 'user', content: text, images }])
      return
    }

    sendMessage(text, images)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send()
    }
  }

  async function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const items = e.clipboardData?.items
    if (!items) return

    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      if (item.type.startsWith('image/')) {
        e.preventDefault()
        const file = item.getAsFile()
        if (file) {
          const reader = new FileReader()
          reader.onload = (event) => {
            const dataUri = event.target?.result as string
            setPastedImages(prev => [...prev.slice(-2), dataUri])  // Keep last 3 images max
          }
          reader.readAsDataURL(file)
        }
      }
    }
  }

  // Draggable divider logic
  const onDividerMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const panel = panelRef.current
    if (!panel) return
    const startY = e.clientY
    const totalFlex = readFlex + writeFlex
    const panelHeight = panel.getBoundingClientRect().height - 48 // subtract header
    const startReadFlex = readFlex

    function onMove(ev: MouseEvent) {
      const delta = ev.clientY - startY
      const deltaFlex = (delta / panelHeight) * totalFlex
      const newRead = Math.max(MIN_READ_PX / panelHeight * totalFlex, Math.min(
        (panelHeight - MIN_WRITE_PX) / panelHeight * totalFlex,
        startReadFlex + deltaFlex
      ))
      setReadFlex(newRead)
      setWriteFlex(totalFlex - newRead)
    }

    function onUp() {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }

    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [readFlex, writeFlex])

  // Collapsed toggle button
  if (!open) {
    return (
      <button
        className="inspector-toggle"
        onClick={() => { setOpen(true); setTimeout(() => textareaRef.current?.focus(), 50) }}
        title="Open HQ chat"
      >
        💬
      </button>
    )
  }

  return (
    <div className="inspector-panel" ref={panelRef} style={{ width: PANEL_WIDTH }}>
      {/* Header */}
      <div className="inspector-header">
        <span className="inspector-label">
          {selectedItems?.length
            ? `${selectedItems.length} item${selectedItems.length === 1 ? '' : 's'} selected`
            : selectedItem?.label
              ? `↳ ${selectedItem.label}`
              : 'HQ Chat'}
        </span>
        <div style={{ display: 'flex', gap: '2px', marginLeft: 'auto', marginRight: 8 }}>
          {[{v: 'claude-sonnet-4-6', l: 'S'}, {v: 'claude-opus-4-7', l: 'O'}].map(m => (
            <button
              key={m.v}
              onClick={() => {
                setSelectedModel(m.v)
                localStorage.setItem('hq-model', m.v)
                if (sessionId) { updateSessionModel(sessionId, m.v).catch(() => {}) }
              }}
              style={{
                padding: '2px 7px', fontSize: 11, border: '1px solid #ccc', borderRadius: 3,
                background: selectedModel === m.v ? '#1976d2' : '#fff',
                color: selectedModel === m.v ? '#fff' : '#333',
                cursor: 'pointer', minWidth: 'auto',
              }}
              title={m.v}
            >
              {m.l}
            </button>
          ))}
        </div>
        <button className="inspector-collapse" onClick={() => setOpen(false)} title="Collapse">
          ✕
        </button>
      </div>

      {/* Read area — message history */}
      <div className="inspector-read" style={{ flex: readFlex }}>
        {messages.length === 0 && !streamingText && (
          <div className="inspector-empty">
            {(selectedItems?.length || selectedItem) ? placeholder : `Ask HQ about this ${view}…`}
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`inspector-message inspector-message--${msg.role}`}>
            {msg.images && msg.images.length > 0 && (
              <div className="inspector-message-images">
                {msg.images.map((img, j) => (
                  <img
                    key={j}
                    src={img}
                    alt={`Image ${j + 1}`}
                    style={{ maxWidth: '100%', maxHeight: '150px', marginBottom: '8px', borderRadius: '4px' }}
                  />
                ))}
              </div>
            )}
            {msg.content}
          </div>
        ))}
        {statusText && !streamingText && (
          <div className="inspector-message inspector-message--status">
            ⏳ {statusText}
          </div>
        )}
        {streamingText && (
          <div className="inspector-message inspector-message--assistant inspector-message--streaming">
            {streamingText}
          </div>
        )}
        {draft && (
          <div style={{ padding: '8px 0' }}>
            <DraftEditor draft={draft} onSent={(_id) => setDraft(null)} />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Draggable divider */}
      <div
        className="inspector-divider"
        ref={dividerRef}
        onMouseDown={onDividerMouseDown}
        title="Drag to resize"
      >
        <span className="inspector-divider-handle">⬍</span>
      </div>

      {/* Write area */}
      <div className="inspector-write" style={{ flex: writeFlex }}>
        {pastedImages.length > 0 && (
          <div className="inspector-image-preview">
            {pastedImages.map((img, i) => (
              <div key={i} style={{ position: 'relative', display: 'inline-block', marginRight: '8px', marginBottom: '8px' }}>
                <img src={img} alt={`Pasted ${i + 1}`} style={{ maxHeight: '60px', borderRadius: '4px' }} />
                <button
                  style={{
                    position: 'absolute', top: '-5px', right: '-5px',
                    width: '20px', height: '20px', padding: 0,
                    borderRadius: '50%', background: '#999', color: '#fff',
                    border: 'none', cursor: 'pointer', fontSize: '12px'
                  }}
                  onClick={() => setPastedImages(prev => prev.filter((_, j) => j !== i))}
                  title="Remove image"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
        <textarea
          ref={textareaRef}
          className="inspector-textarea"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={streaming ? 'Type to queue next message...' : placeholder}
          rows={3}
        />
        <button
          className="inspector-send"
          onClick={send}
          disabled={input.trim().length === 0 && pastedImages.length === 0}
        >
          {streaming ? '+' : '→'}
        </button>
      </div>
    </div>
  )
}
